/**
 * Phase 22A — THE CREATION FORM'S PLAIN LANGUAGE (composition plan §3.4, §3.5.2).
 *
 * Two rules live here, because both are about the WORDS the form uses and both must be testable without a
 * browser:
 *
 * 1. **Ask the type first.** The shop picks what it is selling in plain language — one service over several
 *    visits, a set number of each service, several services done together, or one visit for a party. The type
 *    decides which questions are asked (item 7), so a shop is never asked a question its package does not
 *    have an answer to.
 * 2. **Say what a bundle session is.** A bundle visit is **ONE session**, and a customer who takes only some
 *    of the declared services still spends it (Decision 9, revised 2026-10-06). The form must say *"each visit
 *    is one session"* and must **NOT** say *"all services required"* — the earlier wording, which the user
 *    reversed (item 5).
 *
 * It also builds **the sentence the customer is sold on** (§3.4). That sentence is not decoration: *"a type
 * the builder cannot explain in one sentence is a type that will be mis-sold"*, so a type with no sentence is
 * REFUSED rather than saved with a blank.
 *
 * WHY THIS IS A PURE MODULE. It is copy and arithmetic over a form's answers, so it needs no React, no DOM and
 * no network — which is what lets `frontend/src/utils/__tests__/packageCompositionCopy.test.js` check the
 * wording and the refusal rules directly, and lets the backend spec check that the party cap here still matches
 * the server's.
 */

/** The user's own four types, in the words a shop owner would use rather than the model's own axis names. */
export const TYPE_IDS = {
  SINGLE: 'single',
  FAMILY: 'family',
  BUNDLE: 'bundle',
  COUPLE: 'couple',
};

/**
 * The platform's party cap — the same ceiling `MAX_ADDITIONAL_GROUP_MEMBERS = 2` in
 * `routes/bookingRoutes.js` produces, and the same value as `MAX_PARTY_SIZE` in `routes/packageRoutes.js`.
 * One shared visit cannot cover more people than a booking may contain. Pinned as a literal here because a
 * browser bundle cannot import a server constant; `tests/packageCompositionModel.spec.js` asserts that this
 * literal and the server's still agree, so the two cannot drift apart silently.
 */
export const MAX_PARTY_SIZE = 3;

/**
 * The four types, each mapped to the composition model it is.
 *
 * `name` is the SHORT, UNIQUE label a shop picks from — one word where possible, because two cards that read
 * alike are two cards a shop has to read twice. `description` says what the name means in their words, and
 * `example` makes it concrete. A name with no description would be a label; a description with no name would
 * be a paragraph.
 *
 * `model` is not a convenience: it is the answer, in the plan's own terms, and the server validates it
 * (`validateCompositionModel`). Copying it into the payload is how "Family" means something.
 *
 * `deferred` means the model EXISTS and the server validates it, but the form must not offer it yet. It is a
 * property of the type rather than a filter at the call site, so "not yet" travels with the thing it applies
 * to and cannot be forgotten by a second screen.
 */
export const PACKAGE_TYPES = [
  {
    id: TYPE_IDS.SINGLE,
    name: 'Single',
    description: 'One service, over several visits',
    example: 'Five haircuts for one person',
    // The user's "Single": one service, one session per person.
    model: { holderModel: 'single', sessionContents: 'quota', consumptionModel: 'per_person' },
    legacyType: 'multi_session',
    serviceLines: 'one',
    sessionsQuestion: 'How many visits?',
    sessionsHint: 'Each visit uses one session of the service.',
    asksQuantities: false,
    asksCoveredPeople: false,
  },
  {
    id: TYPE_IDS.FAMILY,
    name: 'Family & group',
    description: 'A set number of each service',
    example: "Three adult cuts and three kids' cuts",
    // The user's "Family / Group": 3 + 3, never 4 + 2 — the quantities ARE the package.
    model: { holderModel: 'single', sessionContents: 'quota', consumptionModel: 'per_person' },
    legacyType: 'multi_session',
    serviceLines: 'many',
    sessionsQuestion: 'How many visits in total?',
    sessionsHint: 'Every session must be attached to a service, so this must equal the quantities below.',
    asksQuantities: true,
    asksCoveredPeople: false,
  },
  {
    id: TYPE_IDS.BUNDLE,
    name: 'Bundle',
    description: 'Several services, done together in one visit',
    example: 'A cut, a beard trim and a hand treatment in one appointment',
    // The user's "Bundle": the whole visit is ONE session.
    model: { holderModel: 'single', sessionContents: 'bundle', consumptionModel: 'per_person' },
    legacyType: 'service_bundle',
    serviceLines: 'many',
    sessionsQuestion: 'How many visits?',
    sessionsHint: 'Each visit is one session, whatever is done during it.',
    asksQuantities: false,
    asksCoveredPeople: false,
  },
  {
    id: TYPE_IDS.COUPLE,
    name: 'Couple',
    description: 'One visit for a party, together',
    example: 'A spa visit for two people on one appointment',
    // The user's "Couple": ONE session shared, one person books, both attend.
    model: { holderModel: 'single', sessionContents: 'quota', consumptionModel: 'shared_per_visit' },
    legacyType: 'multi_session',
    serviceLines: 'one',
    sessionsQuestion: 'How many shared visits?',
    sessionsHint: 'One session covers the whole party, together.',
    asksQuantities: false,
    asksCoveredPeople: true,
    // ── NOT OFFERED YET ──────────────────────────────────────────────────────────────────────────
    // `shared_per_visit` is DEFERRED to sub-phase 22E (§3.4 lists its sentence as deferred, and 22E is where
    // one shared visit must spend ONE session and produce ONE payout). Building a card for it now would
    // invite a shop to sell a package the claim cannot yet size. The model stays declared so 22E is a flag
    // flip rather than a rewrite — and so this deferral cannot be forgotten by a second screen.
    deferred: true,
    deferredReason: 'A package a whole party shares is sub-phase 22E, so it is not offered for sale yet.',
  },
];

/**
 * The types the creation form may offer. `PACKAGE_TYPES` is every model the platform KNOWS, which is a
 * different question — a deferred type must still be understood when a stored package carries it.
 */
export const OFFERED_PACKAGE_TYPES = PACKAGE_TYPES.filter((type) => !type.deferred);

/** The two answers to §3.4's first question. Kept small on purpose: gifting is binary for now. */
export const HOLDER_OPTIONS = [
  { id: 'single', label: 'The buyer, and anyone they book', hint: 'Normal case' },
  { id: 'bearer', label: 'Anyone holding the number', hint: 'A gift — the number is the credential' },
];

export const MAX_BUNDLE_VISITS = null;

/**
 * WHY A MULTI-VISIT BUNDLE IS REFUSED, in the shop's own terms.
 *
 * A bundle's `totalSessions` is the number of VISITS, and the services inside one visit are each `quantity: 1`
 * (§3.5.2). But `utils/packagePricing.js` prices a line as `unitPrice × unitsPerSession × quantity` — so a
 * bundle of four visits computes the price of ONE visit. The customer would be sold four visits for the price
 * of one.
 *
 * The fix is a pricing change (a bundle line must multiply by the visit count, not by `quantity`), which is a
 * money-path change and does not belong in the creation form. Until it lands, the form says so plainly rather
 * than quietly creating a package that cannot be paid for.
 */
/**
 * THE BUNDLE VISIT CAP IS GONE (2026-10-09).
 *
 * A bundle used to be limited to ONE visit, because `utils/packagePricing.js` multiplied a line by `quantity` —
 * always 1 for a bundle — so four visits were priced as one. The engine now multiplies a bundle line by its
 * VISIT count, so the cap has nothing left to protect and the form asks for the visits like any other type.
 *
 * Kept as an exported `null` rather than deleted, so a caller (or a stored payload) that still reads the old
 * name gets an obvious nothing instead of a stale `1` that would silently re-cap a bundle.
 */
export const BUNDLE_VISITS_UNSUPPORTED_MESSAGE = null;

/**
 * The wording §3.5.2 requires, in one place so the form and its test cannot disagree — and so the phrase the
 * user REVERSED cannot reappear.
 *
 * ── AND WHERE THE LOSS LANDS (§7.10 consequence 4, added 2026-10-10) ─────────────────────────────
 *
 * *"The copy must say 'for this visit'."* The sentence this replaced — "you may take fewer of them if you
 * want" — was true but INCOMPLETE: it does not say where the loss lands, which is the difference between
 * losing a lash trim today and losing it from the package. §7.10 settled it with a worked example: a
 * 5-visit bundle, the customer skips the trim on the first visit, and the remaining 4 visits still contain
 * all three services. So the sentence now says it: skipped work is lost **for that visit**, and nothing
 * moves to another visit or another service.
 */
export function bundleVisitNote(visits) {
  const count = Number(visits) || 0;
  const opening = count === 1 ? 'This visit is one session.' : `Each of these ${count} visits is one session.`;
  return `${opening} Your customer may choose to take only some of these services in a visit, and it still uses the session. Anything they skip is lost for that visit — it does not move to another visit or another service.`;
}

/** A service name list rendered the way a person would say it: "a, b and c". */
export function describeList(names = []) {
  const clean = names.filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

/** "5 sessions of Men's Haircut" / "3 sessions of A and 3 sessions of B". */
export function describeQuotaLines(lines = []) {
  const parts = lines
    .filter((line) => line && line.name && Number(line.quantity) > 0)
    .map((line) => {
      const quantity = Number(line.quantity);
      return `${quantity} ${quantity === 1 ? 'session' : 'sessions'} of ${line.name}`;
    });
  return describeList(parts);
}

export function typeById(typeId) {
  return PACKAGE_TYPES.find((type) => type.id === typeId) || null;
}

/**
 * Does this type need the per-service quantities to add up to the visit count?
 *
 * Only a QUOTA does (§3.5.1). A bundle's visits are not the sum of its services — asking them to balance is
 * what made every valid bundle look invalid.
 */
export function typeNeedsQuantities(typeId) {
  const type = typeById(typeId);
  if (!type) return false;
  return type.model.sessionContents === 'quota' && type.asksQuantities;
}

/**
 * THE SENTENCE THE CUSTOMER IS SOLD ON (§3.4).
 *
 * The three sentences in the plan are used as written, with the shop's own numbers substituted. A holder
 * sentence is appended for a gift, because §3.4's FIRST question ("anyone holding the number") decides what
 * the buyer is told and nothing else states it.
 *
 * @returns {string|null} null when the type has no sentence — which the caller must treat as "cannot be sold",
 *   not as "nothing to show".
 */
export function buildCustomerSentence({
  typeId,
  holderModel = 'single',
  lines = [],
  totalSessions = 0,
  coveredPeople = 0,
} = {}) {
  const type = typeById(typeId);
  // A type with no sentence cannot be explained in one sentence, and §3.4 is explicit that such a type will
  // be mis-sold. Returning null (rather than a generic fallback) is what makes the refusal possible.
  if (!type) return null;

  const visits = Number(totalSessions) || 0;
  const sentences = [];

  if (type.model.sessionContents === 'bundle') {
    const names = lines.map((line) => line?.name).filter(Boolean);
    sentences.push(`These services are done together: ${describeList(names)}.`);
    // "for this visit" (§7.10 consequence 4). The visit is one session whether or not every service is taken,
    // and a customer who takes fewer must be told WHERE that loss lands — otherwise "take fewer if you want"
    // reads as banking the ones they skipped.
    sentences.push(
      'Each visit is one session, and you may take fewer of them for this visit. Anything you skip is lost for that visit — it does not move to another visit or another service.',
    );
  } else {
    const quota = describeQuotaLines(lines);
    if (quota) {
      sentences.push(`This covers ${quota}.`);
    } else if (visits > 0) {
      sentences.push(`This covers ${visits} ${visits === 1 ? 'session' : 'sessions'}.`);
    }
    sentences.push(
      'Each person booked takes one session of the service they are booked for, and anyone you book may use it.',
    );
  }

  if (type.model.consumptionModel === 'shared_per_visit') {
    const party = Number(coveredPeople) || 0;
    sentences.push(
      party > 0
        ? `This covers ${party} people together, in one appointment.`
        : 'This covers the party together, in one appointment.',
    );
  }

  if (holderModel === 'bearer') {
    sentences.push('This is a gift: whoever holds the number can use it.');
  }

  return sentences.join(' ');
}

/**
 * The form's own check, BEFORE the round trip.
 *
 * The codes are the SERVER'S codes wherever the server has one, so the form cannot invent a refusal the
 * server would not make, and a shop that sees a code in the UI sees the same code in the API response.
 * `composition_type_required`, `composition_type_unexplainable` and `bundle_multi_visit_unsupported` are
 * client-only, and are named so that is obvious.
 *
 * @returns {{ok: boolean, code: string|null, message: string, errors: Array<{code: string, message: string}>}}
 */
export function validateCompositionChoice({
  typeId,
  lines = [],
  totalSessions = 0,
  coveredPeople = 0,
} = {}) {
  const type = typeById(typeId);
  if (!type) {
    // TWO DIFFERENT GAPS, and they need different answers from the shop. Nothing chosen yet is a question to
    // put to them; an id this form does not recognise is a type it cannot explain — which §3.4 says must not
    // be sold, and which can only arrive from a saved package carrying an older model.
    if (!typeId) {
      return fail(
        'composition_type_required',
        'Choose the kind of package you are selling. It decides what the customer gets and what we ask you next.',
      );
    }
    return fail(
      'composition_type_unexplainable',
      'This kind of package cannot be described to a customer in one sentence yet, so it cannot be sold.',
    );
  }

  if (buildCustomerSentence({ typeId, lines, totalSessions, coveredPeople }) === null) {
    return fail(
      'composition_type_unexplainable',
      'This kind of package cannot be described to a customer in one sentence yet, so it cannot be sold.',
    );
  }

  const serviceLines = lines.filter((line) => line && line.serviceId);
  if (serviceLines.length === 0) {
    return fail('composition_no_services', 'A package needs at least one service.');
  }

  const visits = Number(totalSessions) || 0;

  if (type.model.sessionContents === 'bundle') {
    // §3.5.2 — a bundle declares services, not quantities. A quantity above 1 is the MIXING §7.7 forbids, and
    // since a visit is the unit, there is nothing for a quantity to mean.
    if (serviceLines.some((line) => Number(line.quantity) > 1)) {
      return fail(
        'composition_model_mixed',
        'A bundle is delivered one visit at a time, so no service carries a quantity. Set every service to one.',
      );
    }
    if (visits < 1) {
      return fail('composition_quota_line_missing', 'Say how many visits this package includes.');
    }
    return ok();
  }

  // The quota rule applies ONLY to a quota (§3.5.1).
  if (typeNeedsQuantities(typeId)) {
    if (serviceLines.some((line) => !(Number(line.quantity) > 0))) {
      return fail('composition_quota_line_missing', 'Every service needs a quantity of at least 1.');
    }
    const totalQuantity = serviceLines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    if (visits > 0 && totalQuantity > visits) {
      return fail(
        'composition_quota_exceeds_sessions',
        `Service quantities (${totalQuantity}) cannot exceed total sessions (${visits}). Adjust the quantities or raise the total sessions.`,
      );
    }
    if (visits > 0 && totalQuantity < visits) {
      return fail(
        'composition_quota_short',
        `${visits - totalQuantity} session(s) are not attached to any service. Service quantities (${totalQuantity}) must equal total sessions (${visits}).`,
      );
    }
  }

  if (type.model.consumptionModel === 'shared_per_visit') {
    const party = Number(coveredPeople);
    if (!Number.isInteger(party) || party < 1) {
      return fail(
        'composition_covered_people_required',
        'A package shared by the whole party must say how many people one visit covers.',
      );
    }
    if (party > MAX_PARTY_SIZE) {
      return fail(
        'composition_covered_people_too_many',
        `One visit can cover at most ${MAX_PARTY_SIZE} people. Reduce the covered people, or use one session per person.`,
      );
    }
  }

  return ok();
}

function ok() {
  return { ok: true, code: null, message: '', errors: [] };
}

function fail(code, message) {
  return { ok: false, code, message, errors: [{ code, message }] };
}

/**
 * The payload's composition fields, derived from the answers.
 *
 * `sessionBundleServices` mirrors the service rows for a bundle, because that IS the bundle's declaration —
 * and for a quota it is an empty array, never a leftover.
 */
export function compositionPayload({ typeId, lines = [], totalSessions = 0, coveredPeople = 0, holderModel }) {
  const type = typeById(typeId);
  if (!type) return null;

  const model = {
    ...type.model,
    ...(holderModel ? { holderModel } : {}),
  };

  const isBundle = model.sessionContents === 'bundle';

  return {
    type: type.legacyType,
    holderModel: model.holderModel,
    sessionContents: model.sessionContents,
    consumptionModel: model.consumptionModel,
    coveredPeople: model.consumptionModel === 'shared_per_visit' ? Number(coveredPeople) : null,
    sessionBundleServices: isBundle
      ? lines.map((line) => line?.serviceId).filter(Boolean)
      : [],
    // A bundle's services carry no quota: the visit is the unit, so every line is exactly one.
    services: isBundle
      ? lines.map((line) => ({ ...line, quantity: 1 }))
      : lines,
  };
}
